import * as Dialog from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import {
  Flex,
  Grid,
  Heading,
  IconButton,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { BpSummaryCard } from 'components/common/dialogs/bp-summary/card';
import { BpSummaryToolbar } from 'components/common/dialogs/bp-summary/toolbar';
import { formatDistanceToNow } from 'date-fns';
import { format } from 'date-fns-tz';
import useLatest from 'hooks/useLatestRef';
import { t } from 'i18next';
import { IVerboseComponent } from 'interfaces/i-components';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { BPSortMode } from 'lib_ts/enums/bp-summary.enums';
import {
  LOCAL_DATETIME_FORMAT_LONG,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IBPSummary } from 'lib_ts/interfaces/i-bp-summary';
import { IBpSummaryResponseMsg } from 'lib_ts/interfaces/machine-msg/i-bp-summary';
import { IGenericToggleMsg } from 'lib_ts/interfaces/machine-msg/i-generic';
import { isNil } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { trySocketEmit } from 'services/socket';
import { useAuthStore } from 'stores/use-auth-store';
import useBpSummaryStore from 'stores/use-bp-summary-store';
import useMiscStore from 'stores/use-misc-store';
import { v4 } from 'uuid';

const DESCRIPTION = 'Monitor the BP activity of all machines.';

export const BpSummaryDialog = (props: IVerboseComponent) => {
  const wsConnected = useAuthStore((s) => s.wsConnected);
  const filters = useBpSummaryStore((s) => s.filters);
  const lastUpdated = useBpSummaryStore((s) => s.lastUpdated);
  const open = useBpSummaryStore((s) => s.open);
  const sortMode = useBpSummaryStore((s) => s.sortMode);
  const summaries = useBpSummaryStore((s) => s.summaries);

  const latestSummaries = useLatest(summaries);

  // track open dialog(s)
  useEffect(() => {
    const id = v4();

    useMiscStore.getState().registerDialog({
      identifier: props.identifier,
      v4ID: id,
      open: true,
    });

    const callback = (event: CustomEvent) => {
      const data: IBpSummaryResponseMsg = event.detail;

      const dict = ArrayHelper.firstBy(latestSummaries.current, 'machineID');

      // replace every entry since we assume this is triggered only when updated
      data.summaries.forEach((m) => {
        dict[m.machineID] = m;
      });

      // because compiler is dumb
      const nextSummaries: IBPSummary[] = [];

      for (const summary of Object.values(dict)) {
        if (summary) {
          nextSummaries.push(summary);
        }
      }

      useBpSummaryStore.getState().setSummaries(nextSummaries);
    };

    WebSocketHelper.on(WsMsgType.S2U_BpSummary, callback);

    trySocketEmit<IGenericToggleMsg>(WsMsgType.U2S_BpSummary, { enable: true });

    return () => {
      useMiscStore.getState().registerDialog({
        identifier: props.identifier,
        v4ID: id,
        open: false,
      });

      WebSocketHelper.remove(WsMsgType.S2U_BpSummary, callback);

      trySocketEmit<IGenericToggleMsg>(WsMsgType.U2S_BpSummary, {
        enable: false,
      });
    };
  }, [props.identifier, wsConnected]);

  // filtered and sorted
  const displaySummaries = useMemo(() => {
    const output = summaries
      .filter((m) => m.history.length > 0)
      .filter(
        (m) =>
          isNil(filters.machineIDs) ||
          filters.machineIDs.length === 0 ||
          filters.machineIDs.includes(m.machineID)
      )
      .filter(
        (m) =>
          isNil(filters.teamIDs) ||
          filters.teamIDs.length === 0 ||
          filters.teamIDs.includes(m.teamID)
      )
      .filter(
        (m) =>
          isNil(filters.competitions) ||
          filters.competitions.length === 0 ||
          filters.competitions.includes(m.competition)
      )
      .filter((m) => isNil(filters.inGame) || filters.inGame === m.inGame);

    if (sortMode) {
      switch (sortMode.value as BPSortMode) {
        case BPSortMode.LastFired: {
          output.sort((a, b) => {
            const [aValue, bValue] = [a, b].map((m) => m.lastFired ?? '');
            return sortMode.dir * StringHelper.safeSort(aValue, bValue);
          });
          break;
        }

        case BPSortMode.Machine: {
          output.sort(
            (a, b) =>
              sortMode.dir * StringHelper.safeSort(a.machineID, b.machineID)
          );
          break;
        }

        case BPSortMode.Team: {
          output.sort(
            (a, b) =>
              sortMode.dir * StringHelper.safeSort(a.teamName, b.teamName)
          );
          break;
        }

        default: {
          break;
        }
      }
    }

    return output;
  }, [summaries, filters, sortMode]);

  const themeWrapper = useRef(document.getElementById('ThemeWrapper'));

  return (
    <Dialog.Root
      open={open}
      // called when closed via Esc key
      onOpenChange={() => useBpSummaryStore.getState().toggleOpen()}
      // allows interaction with other elements on the screen while open (e.g. toasts on the side)
      modal={false}
    >
      <Dialog.Portal container={themeWrapper.current}>
        <Dialog.Description>{DESCRIPTION}</Dialog.Description>

        <Dialog.Content
          aria-describedby={DESCRIPTION}
          className="bg-background"
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,

            overflowY: 'auto',
          }}
          // avoid dismissing when clicking on toasts
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Flex direction="column" gap="5" p="4">
            <Dialog.Title className="DialogTitle">
              <Flex pt={RADIX.FLEX.PAD.XS} justify="between">
                <Flex direction="column">
                  <Heading size={RADIX.HEADING.SIZE.MODAL_TITLE} truncate>
                    Live BP Summary
                  </Heading>

                  {lastUpdated && (
                    <Text
                      size="1"
                      color={RADIX.COLOR.SECONDARY}
                      title={format(lastUpdated, LOCAL_DATETIME_FORMAT_LONG, {
                        timeZone: LOCAL_TIMEZONE,
                      })}
                    >
                      Last updated {formatDistanceToNow(lastUpdated)} ago
                    </Text>
                  )}
                </Flex>

                <IconButton
                  color={RADIX.COLOR.NEUTRAL}
                  variant="soft"
                  className="btn-floating"
                  onClick={() => useBpSummaryStore.getState().toggleOpen()}
                >
                  <Cross2Icon />
                </IconButton>
              </Flex>
            </Dialog.Title>

            <BpSummaryToolbar />

            <Skeleton loading={summaries.length === 0} height="50vh">
              {displaySummaries.length === 0 && (
                <Text>{t('common.no-results-found')}</Text>
              )}

              <Grid
                gap="2"
                columns={{
                  md: '1',
                  lg: '2',
                  xl: '3',
                }}
              >
                {displaySummaries.map((m) => (
                  <BpSummaryCard
                    key={`${m.machineID}-${m.lastFired}`}
                    summary={m}
                  />
                ))}
              </Grid>
            </Skeleton>
          </Flex>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
